import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { getRaceTimeline } from '../data/votingRepository';
import type { ElectionState, StateRace } from '../types';
import { colorIndexOf } from '../utils/barRace';
import { votingErrorMessage } from '../utils/votingErrors';
import { BarChartRace } from './BarChartRace';

interface RaceReplayDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  state: ElectionState;
  race: StateRace;
  positionTitle: string;
}

/** Admin: replay a closed round with the full controls (play, pause, replay, speed, timeline) */
export function RaceReplayDialog({ open, onOpenChange, state, race, positionTitle }: RaceReplayDialogProps) {
  const { data: timeline, isLoading, error } = useQuery({
    queryKey: ['voting', 'timeline', race.id],
    queryFn: () => getRaceTimeline(race.id),
    enabled: open,
    staleTime: Infinity,
  });
  const pool = state.candidates.map((c) => c.id);
  const candidates = (race.candidate_ids ?? []).map((id) => {
    const c = state.candidates.find((x) => x.id === id);
    return { id, name: c?.full_name ?? '—', photo: c?.photo_filename ?? null, colorIndex: colorIndexOf(pool, id) };
  });
  const rd = race.result_data;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Carrera de votos · {positionTitle} · Ronda {race.round_number}</DialogTitle>
          <DialogDescription>Así llegaron los votos durante la ronda. Use el reloj para pausar, retroceder o cambiar la velocidad.</DialogDescription>
        </DialogHeader>
        {isLoading || !rd ? (
          <div className="flex justify-center py-10"><Loader2 className="size-6 animate-spin text-muted-foreground" /></div>
        ) : error || !timeline ? (
          <p className="text-sm text-destructive">{votingErrorMessage(error)}</p>
        ) : (
          <BarChartRace
            key={race.id}
            candidates={candidates}
            timeline={timeline}
            baseCount={rd.base_count}
            thresholdPct={Number(race.threshold_pct)}
            winnerId={race.result_candidate_id}
            blankVotes={rd.blank}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
